import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { generateSector } from "./utils/sector";
import { fmt } from "./utils/fmt";
import { loadSave, writeSave, migrateResearchLevels, FUEL_START, FUEL_MAX, VC_START, xpForLevel, FUEL_COST_ADJACENT, FUEL_COST_FAST, FUEL_GAIN_DEPOT } from "./utils/save";
import { useSmeltQueue } from "./hooks/useSmeltQueue";
import { useResearch, computeStatsFromResearch, computeUnlocks } from "./hooks/useResearch";
import { useEchoCards } from "./hooks/useEchoCards";
import { useShipParts } from "./hooks/useShipParts";
import { useTradeShop } from "./hooks/useTradeShop";
import { PART_DROP_CHANCE, rollPartDrop, SHIP_PARTS as SHIP_PARTS_FOR_TOAST } from "./data/shipParts";
import { ECHO_SOURCES } from "./data/echoCards";
import { DEBRIS_ITEMS, BASE_MATERIALS } from "./data/items";
import { HarvestTab } from "./components/HarvestTab";
import { SmelterTab } from "./components/SmelterTab";
import { ResearchTab } from "./components/ResearchTab";
import { EchoCardsTab } from "./components/EchoCardsTab";
import { DevTab } from "./components/DevTab";
import { StarmapTab } from "./components/StarmapTab";
import { TradeTab } from "./components/TradeTab";
import { StatsTab } from "./components/StatsTab";
import { ShipTab } from "./components/ShipTab";
import { InventoryTab } from "./components/InventoryTab";

// TRAVEL_DURATION: ms the loading screen is shown after the player confirms travel
const TRAVEL_DURATION = 900;

// ── Dashboard HUD nav ─────────────────────────────────────────────────────────
// Futuristic spaceship console — icons in a fan arc above a central label strip.
const NAV_ICONS = {
  harvest:   "⚡",
  smelter:   "🔥",
  research:  "🔬",
  echocards: "〰️",
  starmap:   "🗺️",
  stats:     "📊",
  trade:     "🏪",
  ship:      "🚀",
  inventory: "🎒",
  dev:       "⚗️",
};
const NAV_LABELS = {
  harvest:   "HARVEST",
  smelter:   "FORGE",
  research:  "RESEARCH",
  echocards: "RIFT ECHOES",
  starmap:   "MAP",
  stats:     "STATS",
  trade:     "TRADE",
  ship:      "SHIP",
  inventory: "CARGO",
  dev:       "DEV",
};

function DashboardNav({ tabs, activeTab, onTabChange }) {
  const BTN_SIZE = 40;
  const activeTab_ = tabs.find(t => t.id === activeTab) ?? tabs[0];

  return (
    <div style={{
      flexShrink: 0, background: "rgba(4,7,16,0.98)",
      borderTop: "1px solid rgba(255,255,255,0.07)",
      position: "relative", zIndex: 20,
      padding: "8px 6px 10px",
    }}>
      {/* Scanline shimmer overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,200,255,0.012) 3px, rgba(0,200,255,0.012) 4px)",
      }} />

      {/* Row of circle buttons */}
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, position: "relative" }}>
        {tabs.map(t => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              title={NAV_LABELS[t.id] ?? t.id}
              style={{
                width: BTN_SIZE, height: BTN_SIZE,
                borderRadius: "50%",
                background: isActive
                  ? `radial-gradient(circle at 40% 35%, ${t.color}44, ${t.color}18)`
                  : "rgba(255,255,255,0.04)",
                border: isActive
                  ? `2px solid ${t.color}`
                  : "1.5px solid rgba(255,255,255,0.12)",
                boxShadow: isActive
                  ? `0 0 12px ${t.color}55, 0 0 4px ${t.color}33 inset`
                  : "none",
                color: isActive ? t.color : "rgba(255,255,255,0.4)",
                fontSize: 16, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", fontFamily: "inherit",
                transition: "all 0.18s cubic-bezier(0.34,1.56,0.64,1)",
                transform: isActive ? "scale(1.15)" : "scale(1)",
              }}
            >
              {NAV_ICONS[t.id] ?? "◆"}
            </button>
          );
        })}
      </div>

      {/* Active label */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 5 }}>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to right, transparent, rgba(0,200,255,0.15))" }} />
        <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 2, color: activeTab_.color, textShadow: `0 0 8px ${activeTab_.color}66` }}>
          {NAV_LABELS[activeTab] ?? activeTab.toUpperCase()}
        </span>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to left, transparent, rgba(0,200,255,0.15))" }} />
      </div>
    </div>
  );
}

const ALL_TABS = [
  { id: "harvest",   label: "⚡ HARVEST",  color: "#00ccff" },
  { id: "smelter",   label: "🔥 SMELT",    color: "#ff9944" },
  { id: "research",  label: "🔬 RSRCH",    color: "#bb77ff" },
  { id: "echocards", label: "〰️ ECHOES",  color: "#bb77ff" },
  { id: "starmap",   label: "🗺️ MAP",     color: "#33dd99" },
  { id: "stats",     label: "📊 STATS",   color: "#aaddff" },
  { id: "trade",     label: "🏪 TRADE",   color: "#44ffcc" },
  { id: "ship",      label: "🚀 SHIP",    color: "#ff6644" },
  { id: "inventory", label: "🎒 CARGO",   color: "#ffcc44" },
  { id: "dev",       label: "⚗ DEV",      color: "#ffcc00" },
];

export default function VoidHarvester() {
  const saved = useMemo(() => loadSave(), []);

  // ── Persistent state ──────────────────────────────────────────────────────
  const [inventory,      setInventory]      = useState(saved?.inventory    ?? {});
  const [materials,      setMaterials]      = useState(saved?.materials    ?? {});
  const [researchLevels, setResearchLevels] = useState(() => migrateResearchLevels(saved));
  const [stats,          setStats]          = useState(() => computeStatsFromResearch(migrateResearchLevels(saved)));
  const [sectorIndex,    setSectorIndex]    = useState(saved?.sectorIndex  ?? 0);
  const [currentNode,    setCurrentNode]    = useState(saved?.currentNode  ?? 0);
  const [visitedNodes,      setVisitedNodes]      = useState(saved?.visitedNodes      ?? new Set([0]));
  const [discoveredMaterials, setDiscoveredMaterials] = useState(() => saved?.discoveredMaterials ?? new Set());
  const [fuel,           setFuel]           = useState(saved?.fuel         ?? FUEL_START);
  // depotCooldowns: nodeId → unix timestamp when refuel becomes available again
  const [depotCooldowns, setDepotCooldowns] = useState(saved?.depotCooldowns ?? {});
  const [fuelMax]                           = useState(saved?.fuelMax      ?? FUEL_MAX);
  const [voidCreds,      setVoidCreds]      = useState(saved?.voidCreds    ?? VC_START);
  const [xp,             setXp]             = useState(saved?.xp           ?? 0);
  const [xpLevel,        setXpLevel]        = useState(saved?.xpLevel      ?? 1);
  const [onlinePlaytime,  setOnlinePlaytime]  = useState(saved?.onlinePlaytime  ?? 0);
  const [offlinePlaytime, setOfflinePlaytime] = useState(saved?.offlinePlaytime ?? 0);

  // ── Travel loading state ──────────────────────────────────────────────────
  // When the player confirms travel, we show a brief loading overlay, then switch
  // to the harvest tab after TRAVEL_DURATION ms.
  const [traveling, setTraveling] = useState(false);

  // Per-sector flag — flipped to true once the Guardian boss at gate_out dies.
  // Resets on sector change. Gate_out advances the sector only when true.
  const [guardianCleared, setGuardianCleared] = useState(false);

  // ── Ephemeral / UI state ──────────────────────────────────────────────────
  const [tab,             setTab]             = useState("harvest");
  const [smHovered,       setSmHovered]       = useState(null);
  const [nodeActivePerks, setNodeActivePerks] = useState([]);
  const [autoNav,         setAutoNav]         = useState(false);
  const [droneCount,      setDroneCount]      = useState(0);
  const [devSpawnRate,    setDevSpawnRate]     = useState(1);
  const [devQueueMax,     setDevQueueMax]      = useState(1);
  const [devTradeOpen,    setDevTradeOpen]     = useState(false);
  const [devFreeFuel,     setDevFreeFuel]      = useState(false);
  const [devRevealAll,    setDevRevealAll]     = useState(false);
  const [devInstantSmelt, setDevInstantSmelt]  = useState(false);
  const devInstantSmeltRef = useRef(false);
  useEffect(() => { devInstantSmeltRef.current = devInstantSmelt; }, [devInstantSmelt]);
  const [, setDebrisDestroyedAtNode]          = useState(0);
  const [scannerChargesLeft, setScannerChargesLeft] = useState(0);
  // echoToast: { amount, cardName?, key } — shown briefly when Echo Currency or a card is gained
  const [echoToast, setEchoToast] = useState(null);
  const echoToastTimerRef = useRef(null);
  // shipPartToast: { partName, tierName, rarityColor, key }
  const [shipPartToast, setShipPartToast] = useState(null);
  const shipPartToastTimerRef = useRef(null);
  // matDiscoveryToast: { mat, key } — shown when player first discovers a base material
  const [matDiscoveryToast, setMatDiscoveryToast] = useState(null);
  const matDiscoveryTimerRef = useRef(null);
  // tradeOutpostToast: { key } — shown once when first visiting a trade outpost
  const [tradeOutpostToast, setTradeOutpostToast] = useState(null);
  const tradeOutpostToastTimerRef = useRef(null);
  // offlineModal: { gains: { [id]: qty }, totalItems } — shown on mount if idle time > 30s
  const [offlineModal, setOfflineModal] = useState(null);

  // ── Refs shared with canvas loops ─────────────────────────────────────────
  const harvestCanvasRef = useRef(null);
  const statsRef         = useRef(stats);
  const perksRef         = useRef(nodeActivePerks);
  const autoNavRef       = useRef(false);
  const droneCountRef    = useRef(0);
  const devSpawnRateRef  = useRef(1);
  const queueMaxRef      = useRef(1);
  // Compute echo multiplier from active node perks — updated whenever perks change
  const nodeEchoMultRef = useRef(1);
  useEffect(() => {
    let mult = 1;
    for (const pk of nodeActivePerks) {
      if (pk.id === "echo_surge")  mult *= 3;
      if (pk.id === "echo_trace")  mult *= 2;
      if (pk.id === "rift_static") mult *= 0.4;
    }
    nodeEchoMultRef.current = mult;
  }, [nodeActivePerks]);

  useEffect(() => { statsRef.current        = stats;           }, [stats]);
  useEffect(() => { perksRef.current        = nodeActivePerks; }, [nodeActivePerks]);
  useEffect(() => { autoNavRef.current      = autoNav && (stats.autoNavUnlocked ?? false); }, [autoNav, stats.autoNavUnlocked]);
  useEffect(() => { droneCountRef.current   = Math.min(droneCount, stats.droneMax ?? 0);  }, [droneCount, stats.droneMax]);
  useEffect(() => { devSpawnRateRef.current = devSpawnRate;    }, [devSpawnRate]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const sector = useMemo(() => generateSector(sectorIndex, stats.sectorDiversity ?? 0), [sectorIndex, stats.sectorDiversity]);
  const sectorIdxRef = useRef(sectorIndex);
  useEffect(() => { sectorIdxRef.current = sectorIndex; }, [sectorIndex]);

  const currentNodeData = sector.nodes.find(n => n.id === currentNode);
  const isEnemyNode     = currentNodeData?.type === "combat_zone";
  const isTradeNode     = currentNodeData?.type === "trade_outpost";

  useEffect(() => {
    const n = sector.nodes.find(nd => nd.id === currentNode);
    setNodeActivePerks(n ? n.perks : []);
    setDebrisDestroyedAtNode(0);
  }, [currentNode, sector]);

  // Dev: reveal all nodes instantly when toggled on
  useEffect(() => {
    if (devRevealAll) setVisitedNodes(new Set(sector.nodes.map(n => n.id)));
  }, [devRevealAll, sector]);

  // ── Echo Cards system ─────────────────────────────────────────────────────
  const {
    echoCurrency, voidShards,
    ownedCards, equippedCards, boostedCards,
    milestoneMultipliers, cardStatBonuses,
    claimedVoidRewards,
    addEchoCurrency, addVoidShards,
    claimVoidReward,
    rollCardDrop,
    levelUpCard, boostCard, toggleEquipped,
  } = useEchoCards({
    savedEchoCurrency:      saved?.echoCurrency      ?? 0,
    savedVoidShards:        saved?.voidShards        ?? 0,
    savedOwnedCards:        saved?.ownedCards        ?? {},
    savedEquippedCards:     saved?.equippedCards     ?? [],
    savedBoostedCards:      saved?.boostedCards      ?? [],
    savedClaimedVoidRewards: saved?.claimedVoidRewards ?? [],
  });

  // ── Ship parts ────────────────────────────────────────────────────────────
  const {
    ownedTiers, equippedTiers, shipPartBonuses,
    grantShipPart, equipTier,
  } = useShipParts({
    savedOwnedTiers:    saved?.shipOwnedTiers    ?? {},
    savedEquippedTiers: saved?.shipEquippedTiers ?? {},
  });

  // ── Effective stats ───────────────────────────────────────────────────────
  const effectiveStats = useMemo(() => {
    let s = { ...stats };
    // Apply ship part flat bonuses first (additive deltas on top of base stats)
    for (const [key, delta] of Object.entries(shipPartBonuses)) {
      if (typeof delta === "boolean") { s[key] = delta; }
      else { s[key] = (s[key] ?? 0) + delta; }
    }
    // Apply per-stat bonuses from all owned cards (% bonuses as additive multipliers)
    for (const [statKey, bonusPct] of Object.entries(cardStatBonuses)) {
      if (bonusPct === 0) continue;
      // Flat stats (hp, shield, loot) use add; percentage stats use mult
      const flatStats = new Set(["playerMaxHp", "shieldMax", "bonusLoot", "scannerReveal"]);
      if (flatStats.has(statKey)) {
        s[statKey] = (s[statKey] ?? 0) + bonusPct;
      } else if (statKey === "blasterFireRate") {
        // Lower is faster — subtract the % bonus
        s[statKey] = (s[statKey] ?? 30) * (1 - bonusPct / 100);
      } else if (statKey === "tradeBuyMult") {
        // Buy multiplier: bonus = discount %, so mult by (1 - pct/100)
        s[statKey] = (s[statKey] ?? 1) * (1 - bonusPct / 100);
      } else {
        s[statKey] = (s[statKey] ?? 0) * (1 + bonusPct / 100);
      }
    }
    // Apply collection milestone multipliers
    const mm = milestoneMultipliers;
    if (mm.all > 1) {
      const allStats = ["laserDamage","laserRange","attractRange","smeltSpeed","spawnRate","blasterDamage","shipSpeed","playerMaxHp","shieldMax","xpMult","tradeSellBonus","bonusLoot"];
      for (const k of allStats) if (s[k] != null) s[k] *= mm.all;
    }
    if (mm.resource > 1) {
      s.smeltSpeed  = (s.smeltSpeed  ?? 1) * mm.resource;
      s.laserDamage = (s.laserDamage ?? 8) * mm.resource;
    }
    if (mm.combat > 1) {
      s.blasterDamage  = (s.blasterDamage  ?? 12) * mm.combat;
      s.playerMaxHp    = (s.playerMaxHp    ?? 100) * mm.combat;
      s.shieldMax      = (s.shieldMax      ?? 10) * mm.combat;
      s.shipSpeed      = (s.shipSpeed      ?? 1.8) * mm.combat;
    }
    if (mm.echo > 1) {
      s.xpMult = (s.xpMult ?? 1) * mm.echo;
    }
    return s;
  }, [stats, cardStatBonuses, milestoneMultipliers, shipPartBonuses]);
  useEffect(() => { statsRef.current = effectiveStats; }, [effectiveStats]);

  const researchUnlocks = useMemo(() => computeUnlocks(researchLevels), [researchLevels]);

  // ── Offline idle gains (run once on mount) ────────────────────────────────
  useEffect(() => {
    const lastOnline = saved?.lastOnline;
    if (!lastOnline) return;
    const elapsed = Math.min(Date.now() - lastOnline, 8 * 60 * 60 * 1000); // cap at 8h
    if (elapsed < 30_000) return; // ignore if less than 30s

    const node = sector.nodes.find(n => n.id === (saved?.currentNode ?? 0));
    const pool = node?.dropPool ?? ["copper", "hull_debris"];
    const elapsedSec = elapsed / 1000;
    // Estimate: ~1 debris every 3s at base spawn rate, debrisCap limits peak density
    const spawnRate = saved?.stats?.spawnRate ?? 1;
    const debrisCount = Math.floor(elapsedSec / 3 * spawnRate * 0.4); // ~40% efficiency while offline
    const gains = {};
    for (let i = 0; i < debrisCount; i++) {
      const id = pool[Math.floor(Math.random() * pool.length)];
      gains[id] = (gains[id] ?? 0) + 1;
    }
    const totalItems = Object.values(gains).reduce((a, b) => a + b, 0);
    if (totalItems === 0) return;

    setInventory(prev => {
      const next = { ...prev };
      for (const [id, qty] of Object.entries(gains)) next[id] = (next[id] ?? 0) + qty;
      return next;
    });
    setOfflineModal({ gains, elapsed });
    // Credit offline time
    if (elapsed > 0) setOfflinePlaytime(prev => prev + elapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only once on mount

  // ── Online playtime ticker — increments every 10s while tab is active ──────
  useEffect(() => {
    const iv = setInterval(() => setOnlinePlaytime(p => p + 10_000), 10_000);
    return () => clearInterval(iv);
  }, []);

  // ── Auto-save ─────────────────────────────────────────────────────────────
  useEffect(() => {
    writeSave({
      inventory, materials, researchLevels, stats, sectorIndex,
      currentNode, visitedNodes, discoveredMaterials,
      echoCurrency, voidShards, ownedCards, equippedCards, boostedCards, claimedVoidRewards,
      fuel, fuelMax, voidCreds, xp, xpLevel, depotCooldowns,
      shipOwnedTiers: ownedTiers, shipEquippedTiers: equippedTiers,
      onlinePlaytime, offlinePlaytime,
    });
  }, [inventory, materials, researchLevels, stats, sectorIndex, currentNode, visitedNodes, discoveredMaterials, echoCurrency, voidShards, ownedCards, equippedCards, boostedCards, claimedVoidRewards, fuel, fuelMax, voidCreds, xp, xpLevel, depotCooldowns, ownedTiers, equippedTiers, onlinePlaytime, offlinePlaytime]);

  // ── Hooks ─────────────────────────────────────────────────────────────────
  // Stable refs so echo callbacks can be used before addEchoCurrency/showEchoToast are defined
  const onSmeltEchoRef   = useRef(null);
  const onTravelEchoRef  = useRef(null);

  const { smeltQueue, smeltProgress, smeltRunsDone, queueSmelt, removeFromQueue } = useSmeltQueue({
    inventory, materials, setInventory, setMaterials, statsRef, perksRef, queueMaxRef,
    setFuel, fuelMax, instantSmeltRef: devInstantSmeltRef,
    onEchoGained: (amount) => { if (onSmeltEchoRef.current) onSmeltEchoRef.current(amount); },
  });

  const discoveredTradeOutposts = useMemo(() =>
    sector.nodes.filter(n => n.type === "trade_outpost" && visitedNodes.has(n.id)).length,
    [sector, visitedNodes]
  );

  const { doResearch } = useResearch({
    researchLevels, setResearchLevels, inventory, setInventory, materials, setMaterials, setStats,
    discoveredTradeOutposts,
    onFinalNodeMaxed: (resId) => claimVoidReward(`research_final_${resId}`, 1),
  });

  const { shopItems, getOwned, buyItem, sellItem, canReset, resetCountdown, resetShop, cooldownActive } = useTradeShop({
    sectorIndex, currentNode,
    voidCreds, setVoidCreds,
    inventory, setInventory,
    materials, setMaterials,
    stats: effectiveStats,
    unlocks: researchUnlocks,
  });

  // ── Navigation: two-step travel ───────────────────────────────────────────
  // Step 1: player taps a node on the starmap → sets smHovered (preview in panel)
  // Step 2: player taps the TRAVEL button in the detail panel → confirmTravel()
  const handleNodeTap = useCallback((nodeId) => {
    const adj = sector.adj.get(currentNode);
    const isAdjacent = adj && adj.has(nodeId);
    const isVisited = visitedNodes.has(nodeId);
    // Allow selecting adjacent nodes OR already-visited nodes for fast travel
    if (!isAdjacent && !isVisited) return;
    setSmHovered(nodeId);
  }, [sector, currentNode, visitedNodes]);

  // Called when the player presses the TRAVEL button in the StarmapTab panel
  const confirmTravel = useCallback((nodeId) => {
    const adj = sector.adj.get(currentNode);
    const isAdjacent = adj && adj.has(nodeId);
    const isVisited = visitedNodes.has(nodeId);
    if (!isAdjacent && !isVisited) return;
    const tgt = sector.nodes.find(n => n.id === nodeId);

    // Gate_out: two-step behaviour.
    //   1) If the player hasn't yet killed the Guardian: travel TO the gate_out
    //      node (normal adjacent fuel cost) and fight the boss there.
    //   2) Once guardianCleared is true, a second confirm advances the sector.
    if (tgt?.type === "gate_out") {
      if (guardianCleared) {
        setTraveling(true);
        setTimeout(() => {
          setSectorIndex(p => p + 1);
          setCurrentNode(0);
          setVisitedNodes(new Set([0]));
          setFuel(fuelMax);
          setScannerChargesLeft(effectiveStats.scannerCharges ?? 0);
          setGuardianCleared(false);
          setSmHovered(null);
          setTraveling(false);
          setTab("harvest");
        }, TRAVEL_DURATION);
        return;
      }
      // Fall through to normal adjacent-travel path (must be adjacent to approach)
      if (!isAdjacent) return;
    }

    // Check fuel (dev free-fuel bypasses all cost checks)
    if (!devFreeFuel && fuel < FUEL_COST_ADJACENT) return;

    // Small chance of a rift echo on any node travel
    if (Math.random() < 1 / 400) {
      if (onTravelEchoRef.current) onTravelEchoRef.current();
    }

    // Fast-travel to a previously visited (discovered) node
    if (!isAdjacent && isVisited) {
      const ftFree = devFreeFuel || effectiveStats.fastTravelFree;
      if (!ftFree && fuel < FUEL_COST_FAST) return;
      if (!ftFree) setFuel(f => Math.max(0, f - FUEL_COST_FAST));
      setTraveling(true);
      setTimeout(() => {
        setCurrentNode(nodeId);
        const ftTgt = sector.nodes.find(n => n.id === nodeId);
        if (ftTgt?.type === "material_site" && ftTgt.matId) {
          setDiscoveredMaterials(prev => {
            if (prev.has(ftTgt.matId)) return prev;
            const mat = DEBRIS_ITEMS.find(d => d.id === ftTgt.matId);
            if (mat) {
              if (matDiscoveryTimerRef.current) clearTimeout(matDiscoveryTimerRef.current);
              setMatDiscoveryToast({ mat, key: Date.now() });
              matDiscoveryTimerRef.current = setTimeout(() => setMatDiscoveryToast(null), 5000);
            }
            return new Set([...prev, ftTgt.matId]);
          });
        }
        setSmHovered(null);
        setTraveling(false);
        setTab("harvest");
      }, TRAVEL_DURATION);
      return;
    }

    // Normal adjacent travel: deduct fuel and apply depot bonus
    const depotReady = tgt?.type === "fuel_depot" && (Date.now() >= (depotCooldowns[nodeId] ?? 0));
    if (!devFreeFuel) {
      setFuel(f => {
        let next = Math.max(0, f - FUEL_COST_ADJACENT);
        if (depotReady) next = Math.min(fuelMax, next + FUEL_GAIN_DEPOT);
        return next;
      });
    } else if (depotReady) {
      setFuel(f => Math.min(fuelMax, f + FUEL_GAIN_DEPOT));
    }
    if (depotReady) {
      setDepotCooldowns(prev => ({ ...prev, [nodeId]: Date.now() + 30 * 60 * 1000 }));
    }

    // Start travel animation, then land on new node
    const isFirstVisit = !visitedNodes.has(nodeId);
    setTraveling(true);
    setTimeout(() => {
      setCurrentNode(nodeId);
      setVisitedNodes(p => { const nx = new Set(p); nx.add(nodeId); return nx; });
      if (tgt?.type === "material_site" && tgt.matId) {
        setDiscoveredMaterials(prev => {
          if (prev.has(tgt.matId)) return prev;
          const mat = DEBRIS_ITEMS.find(d => d.id === tgt.matId);
          if (mat) {
            if (matDiscoveryTimerRef.current) clearTimeout(matDiscoveryTimerRef.current);
            setMatDiscoveryToast({ mat, key: Date.now() });
            matDiscoveryTimerRef.current = setTimeout(() => setMatDiscoveryToast(null), 5000);
          }
          return new Set([...prev, tgt.matId]);
        });
      }
      if (tgt?.type === "trade_outpost" && isFirstVisit) {
        if (tradeOutpostToastTimerRef.current) clearTimeout(tradeOutpostToastTimerRef.current);
        setTradeOutpostToast({ key: Date.now() });
        tradeOutpostToastTimerRef.current = setTimeout(() => setTradeOutpostToast(null), 5000);
      }
      setSmHovered(null);
      setTraveling(false);
      setTab("harvest");
    }, TRAVEL_DURATION);
  }, [sector, currentNode, visitedNodes, fuel, fuelMax, effectiveStats, devFreeFuel, guardianCleared, depotCooldowns]);

  // ── Scanner: reset charges when research level changes or on first load ──────
  useEffect(() => {
    setScannerChargesLeft(effectiveStats.scannerCharges ?? 0);
  }, [effectiveStats.scannerCharges]);

  // Scan: reveal N hidden adjacent nodes without travelling to them
  const handleScan = useCallback(() => {
    setScannerChargesLeft(c => {
      if (c <= 0) return c;
      const reveal = effectiveStats.scannerReveal ?? 1;
      const adj = sector.adj.get(currentNode);
      const hidden = [];
      if (adj) {
        for (const nid of adj) {
          if (!visitedNodes.has(nid)) hidden.push(nid);
        }
      }
      // Shuffle and take up to `reveal` nodes
      for (let i = hidden.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [hidden[i], hidden[j]] = [hidden[j], hidden[i]];
      }
      const toReveal = hidden.slice(0, reveal);
      if (toReveal.length > 0) {
        setVisitedNodes(prev => {
          const next = new Set(prev);
          toReveal.forEach(id => next.add(id));
          return next;
        });
      }
      return c - 1;
    });
  }, [sector, currentNode, visitedNodes, effectiveStats.scannerReveal]);

  // ── On player death: return to node 0, keep visited nodes ───────────────
  const handlePlayerDeath = useCallback(() => {
    setTimeout(() => {
      setCurrentNode(0);
    }, 2200);
  }, []);

  // ── Effective smelt queue cap ─────────────────────────────────────────────
  const effectiveQueueMax = Math.max(effectiveStats.smeltQueueMax ?? 1, devQueueMax);
  useEffect(() => { queueMaxRef.current = effectiveQueueMax; }, [effectiveQueueMax]);

  // ── Echo toast ────────────────────────────────────────────────────────────
  const showEchoToast = useCallback((amount, cardName) => {
    if (echoToastTimerRef.current) clearTimeout(echoToastTimerRef.current);
    setEchoToast({ amount, cardName, key: Date.now() });
    echoToastTimerRef.current = setTimeout(() => setEchoToast(null), 3500);
  }, []);

  // ── Ship part drop helper ─────────────────────────────────────────────────
  const tryDropShipPart = useCallback((chance) => {
    if (Math.random() >= chance) return;
    const { partId, tierIdx } = rollPartDrop();
    const isNew = grantShipPart(partId, tierIdx);
    if (isNew) {
      const part = SHIP_PARTS_FOR_TOAST.find(p => p.id === partId);
      if (!part) return;
      const tier = part.tiers[tierIdx];
      if (shipPartToastTimerRef.current) clearTimeout(shipPartToastTimerRef.current);
      setShipPartToast({ partName: part.name, tierName: tier.name, rarityColor: tier.color, icon: part.icon, key: Date.now() });
      shipPartToastTimerRef.current = setTimeout(() => setShipPartToast(null), 4000);
    }
  }, [grantShipPart]);

  const handleEnemyKill = useCallback(() => {
    tryDropShipPart(PART_DROP_CHANCE.kill);
    const src = ECHO_SOURCES.combatStd;
    const freqBonus = 1 + (effectiveStatsRef.current.echoFrequency ?? 0);
    if (Math.random() < src.cardChance * freqBonus) {
      const newCard = rollCardDrop("combat");
      if (newCard) showEchoToast(0, newCard.name);
    } else {
      const base = Math.round(src.min + Math.random() * (src.max - src.min));
      const amount = base + (effectiveStatsRef.current.echoCurrencyBonus ?? 0);
      addEchoCurrency(amount);
    }
  }, [tryDropShipPart, rollCardDrop, showEchoToast, addEchoCurrency]);

  const handleItemDrop = useCallback((itemId) => {
    // Discover material if it's a discoverable base mat not yet seen
    const item = DEBRIS_ITEMS.find(d => d.id === itemId && d.category === "discoverable");
    if (item) {
      setDiscoveredMaterials(prev => {
        if (prev.has(itemId)) return prev;
        if (matDiscoveryTimerRef.current) clearTimeout(matDiscoveryTimerRef.current);
        setMatDiscoveryToast({ mat: item, key: Date.now() });
        matDiscoveryTimerRef.current = setTimeout(() => setMatDiscoveryToast(null), 5000);
        return new Set([...prev, itemId]);
      });
    }
    // Echo Currency drop from mining (T1 debris)
    const src = ECHO_SOURCES.mineT1;
    const freqBonus = 1 + (effectiveStatsRef.current.echoFrequency ?? 0);
    if (Math.random() < src.cardChance * nodeEchoMultRef.current * freqBonus) {
      const newCard = rollCardDrop(itemId);
      if (newCard) showEchoToast(0, newCard.name);
    } else {
      const base = Math.round((src.min + Math.random() * (src.max - src.min)) * nodeEchoMultRef.current);
      const amount = base + (effectiveStatsRef.current.echoCurrencyBonus ?? 0);
      addEchoCurrency(amount);
    }
    // Chance to find a ship part from mining
    tryDropShipPart(PART_DROP_CHANCE.mine);
  }, [addEchoCurrency, rollCardDrop, showEchoToast, tryDropShipPart]);

  // Wire echo refs now that addEchoCurrency + showEchoToast are in scope
  useEffect(() => {
    onSmeltEchoRef.current = () => {
      const src = ECHO_SOURCES.smelt;
      const base = Math.round(src.min + Math.random() * (src.max - src.min));
      const amount = base + (effectiveStatsRef.current.echoCurrencyBonus ?? 0);
      addEchoCurrency(amount);
    };
    onTravelEchoRef.current = () => {
      const src = ECHO_SOURCES.sector;
      const base = Math.round(src.min + Math.random() * (src.max - src.min));
      const amount = base + (effectiveStatsRef.current.echoCurrencyBonus ?? 0);
      addEchoCurrency(amount);
      showEchoToast(amount);
    };
  }, [addEchoCurrency, showEchoToast]);

  // ── XP system ─────────────────────────────────────────────────────────────
  // Use refs so callbacks always see current values without stale closures.
  const xpRef          = useRef({ xp, xpLevel });
  const onLevelUpRef   = useRef(null); // set by HarvestTab to trigger in-game heal FX
  const effectiveStatsRef = useRef(effectiveStats);
  useEffect(() => { xpRef.current = { xp, xpLevel }; }, [xp, xpLevel]);
  useEffect(() => { effectiveStatsRef.current = effectiveStats; }, [effectiveStats]);

  const addXp = useCallback((amount) => {
    const s = effectiveStatsRef.current;
    const scaled = Math.round(amount * (s.xpMult ?? 1));
    let { xp: cur, xpLevel: lvl } = xpRef.current;
    cur += scaled;
    let levelsGained = 0;
    while (cur >= xpForLevel(lvl)) {
      cur -= xpForLevel(lvl);
      lvl++;
      levelsGained++;
    }
    xpRef.current = { xp: cur, xpLevel: lvl };
    setXp(cur);
    setXpLevel(lvl);
    // Level-up surge rewards
    if (levelsGained > 0) {
      const healAmt = (s.xpLevelUpHeal ?? 0) * levelsGained;
      const fuelAmt = (s.xpLevelUpFuel ?? 0) * levelsGained;
      if (healAmt > 0 && onLevelUpRef.current) onLevelUpRef.current(healAmt);
      if (fuelAmt > 0) setFuel(f => Math.min(fuelMax, f + fuelAmt));
    }
  }, [fuelMax]);

  // Only show the TRADE tab when docked at a trade outpost (or forced open via dev tool)
  const visibleTabs = ALL_TABS.filter(t => t.id !== "trade" || isTradeNode || devTradeOpen);

  const handleTabChange = useCallback((tabId) => setTab(tabId), []);

  return (
    <div style={{ width: "100%", height: "100dvh", background: "#0a0a0f", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 430, height: "100%", maxHeight: 932, background: "#060a10", display: "flex", flexDirection: "column", fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: "#ddeeff", overflow: "hidden", boxShadow: "0 0 0 1px rgba(255,255,255,0.06), 0 8px 40px rgba(0,0,0,0.8)", position: "relative" }}>

        {/* ── Echo toast ── */}
        {echoToast && (
          <div key={echoToast.key} style={{
            position: "absolute", bottom: 80, left: "50%", transform: "translateX(-50%)",
            zIndex: 200, pointerEvents: "none",
            animation: "echoToastIn 0.35s cubic-bezier(0.22,1,0.36,1) forwards",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 9,
              background: "rgba(8,6,24,0.96)", border: "1px solid rgba(187,119,255,0.4)",
              borderRadius: 10, padding: "9px 14px",
              boxShadow: "0 0 20px rgba(187,119,255,0.2), 0 4px 24px rgba(0,0,0,0.6)",
              backdropFilter: "blur(10px)",
            }}>
              <span style={{ fontSize: 20 }}>{echoToast.cardName ? "🃏" : "〰️"}</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#bb77ff", marginBottom: 1 }}>
                  {echoToast.cardName ? "CARD DROPPED" : "ECHO CURRENCY"}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#eef5ff" }}>
                  {echoToast.cardName ? echoToast.cardName : `+${echoToast.amount} Echoes`}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ── Ship part drop toast ── */}
        {shipPartToast && (
          <div key={shipPartToast.key} style={{
            position: "absolute", bottom: 130, left: "50%", transform: "translateX(-50%)",
            zIndex: 200, pointerEvents: "none",
            animation: "echoToastIn 0.35s cubic-bezier(0.22,1,0.36,1) forwards",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 9,
              background: "rgba(8,14,10,0.97)", border: `1px solid ${shipPartToast.rarityColor}66`,
              borderRadius: 10, padding: "9px 14px",
              boxShadow: `0 0 20px ${shipPartToast.rarityColor}33, 0 4px 24px rgba(0,0,0,0.6)`,
              backdropFilter: "blur(10px)",
            }}>
              <span style={{ fontSize: 22 }}>{shipPartToast.icon}</span>
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
                  color: shipPartToast.rarityColor, marginBottom: 2 }}>
                  SHIP PART FOUND
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#eef5ff" }}>
                  {shipPartToast.tierName}
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
                  {shipPartToast.partName}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Trade outpost discovery toast ── */}
        {tradeOutpostToast && (
          <div key={tradeOutpostToast.key} style={{
            position: "absolute", top: 54, left: "50%", transform: "translateX(-50%)",
            zIndex: 201, pointerEvents: "none",
            animation: "matDiscoverIn 0.4s cubic-bezier(0.22,1,0.36,1) forwards",
            width: "calc(100% - 32px)", maxWidth: 340,
          }}>
            <div style={{
              background: "rgba(4,20,18,0.97)", border: "1px solid rgba(51,255,204,0.55)",
              borderRadius: 12, padding: "12px 16px",
              boxShadow: "0 0 28px rgba(51,255,204,0.22), 0 4px 24px rgba(0,0,0,0.7)",
              backdropFilter: "blur(12px)",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#33ffcc", marginBottom: 6 }}>
                🏪 TRADE OUTPOST DISCOVERED
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 28, filter: "drop-shadow(0 0 8px #33ffcc)" }}>🏪</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#33ffcc" }}>Trade Outpost</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                    Buy &amp; sell resources for Void-Creds
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Material discovery toast ── */}
        {matDiscoveryToast && (
          <div key={matDiscoveryToast.key} style={{
            position: "absolute", top: 54, left: "50%", transform: "translateX(-50%)",
            zIndex: 201, pointerEvents: "none",
            animation: "matDiscoverIn 0.4s cubic-bezier(0.22,1,0.36,1) forwards",
            width: "calc(100% - 32px)", maxWidth: 340,
          }}>
            <div style={{
              background: "rgba(8,4,22,0.97)", border: "1px solid rgba(170,85,255,0.55)",
              borderRadius: 12, padding: "12px 16px",
              boxShadow: "0 0 28px rgba(170,85,255,0.28), 0 4px 24px rgba(0,0,0,0.7)",
              backdropFilter: "blur(12px)",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#aa55ff", marginBottom: 6 }}>
                ✨ MATERIAL DISCOVERED
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 28, filter: "drop-shadow(0 0 8px #aa55ff)" }}>
                  {matDiscoveryToast.mat.icon}
                </span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: matDiscoveryToast.mat.color }}>
                    {matDiscoveryToast.mat.name}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                    New recipes unlocked in the 🔥 Smelt tab
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Offline idle gains modal ── */}
        {offlineModal && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 300,
            background: "rgba(4,8,18,0.88)", backdropFilter: "blur(10px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "rgba(8,14,28,0.98)", border: "1px solid rgba(0,200,255,0.35)",
              borderRadius: 14, padding: "20px 22px", width: "calc(100% - 48px)", maxWidth: 360,
              boxShadow: "0 0 32px rgba(0,200,255,0.18), 0 8px 40px rgba(0,0,0,0.8)",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#00ccff", marginBottom: 4 }}>
                ⏰ OFFLINE GAINS
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 14 }}>
                Away for {Math.round(offlineModal.elapsed / 60000)}m — your drones kept harvesting
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
                {Object.entries(offlineModal.gains).map(([id, qty]) => {
                  const meta = DEBRIS_ITEMS.find(d => d.id === id) ?? BASE_MATERIALS.find(m => m.id === id);
                  return (
                    <div key={id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{meta?.icon ?? "📦"}</span>
                      <span style={{ fontSize: 13, color: meta?.color ?? "#aaccee", fontWeight: 700 }}>
                        {meta?.name ?? id}
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 13, color: "#eef5ff", fontWeight: 800 }}>
                        +{fmt(qty)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button
                onClick={() => setOfflineModal(null)}
                style={{
                  width: "100%", padding: "10px 0", borderRadius: 8, border: "none",
                  background: "rgba(0,200,255,0.18)", color: "#00ccff",
                  fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.5,
                }}
              >
                COLLECT
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes echoToastIn {
            from { opacity: 0; transform: translateX(-50%) translateY(12px) scale(0.92); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1);    }
          }
          @keyframes matDiscoverIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-10px) scale(0.94); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1);    }
          }
        `}</style>

        {/* ── Travel loading overlay ── */}
        {traveling && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 100,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            background: "rgba(4,8,18,0.96)", backdropFilter: "blur(8px)",
          }}>
            <div style={{ fontSize: 36, marginBottom: 18, animation: "spin 1.2s linear infinite" }}>🚀</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#33ccff", letterSpacing: 1.5, marginBottom: 8 }}>TRAVELING</div>
            <div style={{ width: 160, height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", background: "#33ccff", borderRadius: 2, animation: `fillBar ${TRAVEL_DURATION}ms linear forwards` }} />
            </div>
            <style>{`
              @keyframes spin { from { transform: rotate(0deg) translateX(12px); } to { transform: rotate(360deg) translateX(12px); } }
              @keyframes fillBar { from { width: 0%; } to { width: 100%; } }
            `}</style>
          </div>
        )}

        {/* ── Persistent top bar: VC + XP + inventory toggle ── */}
        {(() => {
          const needed = xpForLevel(xpLevel);
          const pct    = needed > 0 ? xp / needed : 0;
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", background: "rgba(4,8,18,0.97)", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0, zIndex: 21 }}>
                {/* Void-Creds */}
                <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(51,255,204,0.08)", border: "1px solid rgba(51,255,204,0.22)", borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: 12 }}>💎</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "#33ffcc", letterSpacing: 0.5 }}>{fmt(voidCreds)} VC</span>
                </div>
                {/* XP bar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#bb99ff", letterSpacing: 0.5 }}>LVL {xpLevel}</span>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>{fmt(xp)}/{fmt(needed)} XP</span>
                  </div>
                  <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct * 100}%`, background: "linear-gradient(90deg,#7744ff,#bb99ff)", borderRadius: 2, transition: "width 0.4s ease", boxShadow: "0 0 6px #9966ff88" }} />
                  </div>
                </div>
                {/* Cargo shortcut */}
                <button onClick={() => setTab("inventory")} style={{ background: tab === "inventory" ? "rgba(255,204,68,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${tab === "inventory" ? "rgba(255,204,68,0.45)" : "rgba(255,255,255,0.12)"}`, borderRadius: 7, padding: "4px 8px", color: tab === "inventory" ? "#ffcc44" : "rgba(255,255,255,0.5)", fontFamily: "inherit", fontSize: 16, cursor: "pointer", lineHeight: 1 }}>
                  🎒
                </button>
              </div>
            </>
          );
        })()}

        {/* Harvest — always mounted so canvas loop stays alive */}
        <div style={{ flex: 1, display: tab === "harvest" ? "flex" : "none", flexDirection: "column", position: "relative", overflow: "hidden" }}>
          <HarvestTab
            inventory={inventory} materials={materials}
            nodeActivePerks={nodeActivePerks}
            statsRef={statsRef} perksRef={perksRef}
            autoNavRef={autoNavRef} droneCountRef={droneCountRef} devSpawnRateRef={devSpawnRateRef}
            setInventory={setInventory} setMaterials={setMaterials}
            canvasRef={harvestCanvasRef}
            currentNode={currentNode}
            nodeType={currentNodeData?.type ?? "default"}
            nodeMat={currentNodeData?.matId ?? null}
            nodeDropPool={currentNodeData?.dropPool ?? null}
            nodeRefinedPool={currentNodeData?.refinedPool ?? null}
            onDebrisDestroyed={() => { if (currentNode !== 0) setDebrisDestroyedAtNode(c => c + 1); }}
            onItemDrop={handleItemDrop}
            onEnemyKill={handleEnemyKill}
            isEnemyNode={isEnemyNode}
            onPlayerDeath={handlePlayerDeath}
            sectorIdxRef={sectorIdxRef}
            addXp={addXp}
            onLevelUpRef={onLevelUpRef}
            nodeEchoMultRef={nodeEchoMultRef}
            setVoidCreds={setVoidCreds}
            onGuardianDown={() => setGuardianCleared(true)}
            autoNav={autoNav} setAutoNav={setAutoNav}
            droneCount={droneCount} setDroneCount={setDroneCount}
            stats={effectiveStats}
            discoveredMaterials={discoveredMaterials}
          />
        </div>

        {/* Smelter */}
        <div style={{ flex: 1, display: tab === "smelter" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <SmelterTab
            inventory={inventory} materials={materials}
            researchUnlocks={researchUnlocks} stats={effectiveStats}
            discoveredMaterials={discoveredMaterials}
            smeltQueue={smeltQueue} smeltProgress={smeltProgress} smeltRunsDone={smeltRunsDone}
            queueSmelt={queueSmelt} removeFromQueue={removeFromQueue} queueMax={effectiveQueueMax}
          />
        </div>

        {/* Research */}
        <div style={{ flex: 1, display: tab === "research" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <ResearchTab
            inventory={inventory} materials={materials}
            researchLevels={researchLevels} doResearch={doResearch}
            discoveredTradeOutposts={discoveredTradeOutposts}
          />
        </div>

        {/* Echo Cards */}
        <div style={{ flex: 1, display: tab === "echocards" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <EchoCardsTab
            echoCurrency={echoCurrency}
            voidShards={voidShards}
            ownedCards={ownedCards}
            equippedCards={equippedCards}
            boostedCards={boostedCards}
            milestoneMultipliers={milestoneMultipliers}
            levelUpCard={levelUpCard}
            boostCard={boostCard}
            toggleEquipped={(id) => toggleEquipped(id, effectiveStats.activeCardSlots ?? 1)}
            stats={effectiveStats}
          />
        </div>

        {/* Starmap */}
        <div style={{ flex: 1, display: tab === "starmap" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <StarmapTab
            sector={sector} sectorIndex={sectorIndex}
            currentNode={currentNode} visitedNodes={visitedNodes} discoveredMaterials={discoveredMaterials}
            smHovered={smHovered} setSmHovered={setSmHovered}
            handleNodeTap={handleNodeTap}
            confirmTravel={confirmTravel}
            nodeActivePerks={nodeActivePerks}
            onNodeVisited={() => {}}
            fuel={fuel} fuelMax={fuelMax}
            fuelCostAdjacent={FUEL_COST_ADJACENT} fuelCostFast={FUEL_COST_FAST}
            scannerCharges={scannerChargesLeft}
            onScan={handleScan}
            fastTravelFree={devFreeFuel || effectiveStats.fastTravelFree}
            guardianCleared={guardianCleared}
            depotCooldowns={depotCooldowns}
          />
        </div>

        {/* Stats */}
        <div style={{ flex: 1, display: tab === "stats" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <StatsTab
            effectiveStats={effectiveStats}
            baseStats={stats}
            xp={xp} xpLevel={xpLevel} xpForLevel={xpForLevel}
            ownedTiers={ownedTiers}
            equippedTiers={equippedTiers}
            shipPartBonuses={shipPartBonuses}
            cardStatBonuses={cardStatBonuses}
            onlinePlaytime={onlinePlaytime}
            offlinePlaytime={offlinePlaytime}
          />
        </div>

        {/* Ship */}
        <div style={{ flex: 1, display: tab === "ship" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <ShipTab
            effectiveStats={effectiveStats}
            ownedTiers={ownedTiers}
            equippedTiers={equippedTiers}
            onEquipTier={equipTier}
          />
        </div>

        {/* Inventory / Cargo Manifest */}
        <div style={{ flex: 1, display: tab === "inventory" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <InventoryTab
            inventory={inventory}
            materials={materials}
            discoveredMaterials={discoveredMaterials}
          />
        </div>

        {/* Trade — only visible at trade_outpost nodes */}
        <div style={{ flex: 1, display: tab === "trade" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <TradeTab
            shopItems={shopItems} getOwned={getOwned}
            buyItem={buyItem} sellItem={sellItem}
            voidCreds={voidCreds}
            inventory={inventory} materials={materials}
            canReset={canReset} resetCountdown={resetCountdown}
            resetShop={resetShop} cooldownActive={cooldownActive}
          />
        </div>

        {/* Dev */}
        <div style={{ flex: 1, display: tab === "dev" ? "flex" : "none", flexDirection: "column", overflow: "hidden" }}>
          <DevTab
            autoNav={autoNav} setAutoNav={setAutoNav}
            droneCount={droneCount} setDroneCount={setDroneCount}
            devSpawnRate={devSpawnRate} setDevSpawnRate={setDevSpawnRate}
            devQueueMax={devQueueMax} setDevQueueMax={setDevQueueMax}
            openTrade={() => { setDevTradeOpen(true); setTab("trade"); }}
            devFreeFuel={devFreeFuel} setDevFreeFuel={setDevFreeFuel}
            devRevealAll={devRevealAll} setDevRevealAll={setDevRevealAll}
            devInstantSmelt={devInstantSmelt} setDevInstantSmelt={setDevInstantSmelt}
            addEchoCurrency={addEchoCurrency}
            addVoidShards={addVoidShards}
            rollCardDrop={rollCardDrop}
            simulateOffline={(ms) => {
              const node = sector.nodes.find(n => n.id === currentNode);
              const pool = node?.dropPool ?? ["copper", "hull_debris"];
              const elapsedSec = ms / 1000;
              const spawnRate = effectiveStats.spawnRate ?? 1;
              const debrisCount = Math.floor(elapsedSec / 3 * spawnRate * 0.4);
              const gains = {};
              for (let i = 0; i < debrisCount; i++) {
                const id = pool[Math.floor(Math.random() * pool.length)];
                gains[id] = (gains[id] ?? 0) + 1;
              }
              if (Object.values(gains).reduce((a, b) => a + b, 0) === 0) return;
              setInventory(prev => {
                const next = { ...prev };
                for (const [id, qty] of Object.entries(gains)) next[id] = (next[id] ?? 0) + qty;
                return next;
              });
              setOfflineModal({ gains, elapsed: ms });
            }}
            resetStarmap={() => {
              setVisitedNodes(new Set([0]));
              setCurrentNode(0);
              setSmHovered(null);
            }}
          />
        </div>

        {/* ── Dashboard HUD nav ── */}
        <DashboardNav tabs={visibleTabs} activeTab={tab} onTabChange={handleTabChange} />

      </div>
    </div>
  );
}
